import { SectionCard } from "shared/app/SectionCard";
import { Input } from "shared/components/forms/Input";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { propertyTypeOptions } from "../AddProperty/constants";
import { Select, Show, TextArea } from "shared/components";
import { usePlacesWidget } from "react-google-autocomplete";
import { googlePlaceToAddress } from "shared/addressUtils";
import config from "config/config";
import { states } from "utils/stateCodes";
import { ReactNode } from "react";
import { useAuthContext } from "providers/Auth";
import { PartnerFeatures } from "shared/types";

interface PropertyGeneralFormProps {
  headerExtra?: ReactNode;
  handleChange: any;
  register: any;
  footer?: ReactNode;
  isDefaultProperty?: boolean;
  propertyKindDisabled?: boolean;
}

export const PropertyGeneralForm = ({
  handleChange,
  register,
  headerExtra,
  footer,
  isDefaultProperty,
  propertyKindDisabled
}: PropertyGeneralFormProps) => {
  const dictionary = useUnitsPropertiesDictionary();
  const { type, partnerFeatures } = useAuthContext();
  const hasPropertyOwner = partnerFeatures.includes(
    PartnerFeatures.PropertyOwner
  );

  const { ref } = usePlacesWidget({
    apiKey: config.placesApiKey,
    onPlaceSelected: (place) => {
      const addressFields = googlePlaceToAddress(place);
      if (addressFields) {
        handleChange("address1")(addressFields.address1 || "");
        handleChange("postal_code")(addressFields.postalCode || "");
        handleChange("city")(addressFields.city || "");
        handleChange("state_or_province")(addressFields.stateOrProvince || "");
        handleChange("country")(addressFields.country || "");
      }
    },
    options: {
      types: ["street_address"],
      fields: ["formatted_address", "address_components"],
      componentRestrictions: { country: "us" }
    }
  });

  return (
    <SectionCard title="General" headerExtra={headerExtra} footer={footer}>
      <div className="divide-y divide-alpha-40">
        <div className="grid grid-cols-2 gap-4 pb-4">
          <div>
            <Input
              id="property-name"
              label={`${dictionary.Property} name`}
              required
              placeholder="Lemon Grove Apartments"
              {...register("name")}
            />
          </div>

          <Show if={type.isBoomCRM}>
            <div>
              <Select
                id="property-type"
                label={`${dictionary.Property} type`}
                required
                defaultValue={propertyTypeOptions[0].value}
                options={propertyTypeOptions}
                isDisabled={propertyKindDisabled}
                {...register("kind")}
              />
            </div>
          </Show>

          <Show if={hasPropertyOwner}>
            <div>
              <Input
                id="property-owner-name"
                label="Owner"
                placeholder="Rentex"
                required
                {...register("owner_name")}
              />
            </div>
          </Show>
        </div>

        <div className="grid grid-cols-2 gap-4 py-4">
          <div className="w-full">
            <Input
              id="selected-place"
              label="Street address"
              required
              placeholder="2972 Westheimer Rd"
              ref={ref}
              {...register("address1")}
            />
          </div>
          <div className="w-full">
            <Input
              id="address2"
              placeholder="Apartment 12"
              label="Apartment, suite, etc."
              {...register("address2")}
            />
          </div>
          <div className="w-full">
            <Input
              id="city"
              label="City"
              required
              placeholder="Austin"
              {...register("city")}
            />
          </div>
          <div className="flex w-full justify-end gap-4">
            <div className="mb-auto h-fit flex-1">
              <Select
                id="state-or-province"
                label="State"
                placeholder="State"
                searchable
                hideOptional
                options={states}
                {...register("state_or_province")}
                showErrorMessage
              />
            </div>
            <div className="flex-1">
              <Input
                id="postal-code"
                required
                label="Zip"
                placeholder="11111"
                {...register("postal_code")}
              />
            </div>
          </div>
        </div>

        {isDefaultProperty ? null : (
          <div className="py-4">
            <TextArea
              id="property-description"
              label={
                <div className="flex items-center gap-1">
                  <p>Description</p>
                  <p className="text-sm font-medium text-content-100">
                    (Optional)
                  </p>
                </div>
              }
              placeholder="240"
              rows={5}
              {...register("description")}
            />
          </div>
        )}

        <div className="pt-4 grid grid-cols-2 gap-4">
          <div className="w-full">
            <Input
              id="property-id"
              label={`${dictionary.Property} ID`}
              placeholder="434343"
              {...register("external_id")}
            />
          </div>
        </div>
      </div>
    </SectionCard>
  );
};
